import { Vector3 } from 'three';
import { EWall } from '../../@types/Enums';
import { DEFAULT_WALL_DEPTH, UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import { calculateWallLength } from '../../utils/vectorUtils';
import Wall, { IWall } from '../abstract/Wall';
import { IAttribute } from './AttributeModel';
import { IDoor } from './DoorModel';
import { IPassage } from './PassageModel';
import { IRadiator } from './RadiatorModel';
import { IWindow } from './WindowModel';

export interface IStandardWall extends IWall {
  outerStartPoint: Vector3;
  outerEndPoint: Vector3;
  outerStartPoint2: Vector3;
  outerEndPoint2: Vector3;
  sideALength: number; // Value in meter!
  sideBLength: number; // Value in meter!
  updateWallLengths(): void;
}

class StandardWallModel extends Wall implements IStandardWall {
  outerStartPoint: Vector3;
  outerEndPoint: Vector3;
  outerStartPoint2: Vector3;
  outerEndPoint2: Vector3;
  sideALength: number;
  sideBLength: number;

  constructor(
    startPoint: Vector3,
    endPoint: Vector3,
    depth = DEFAULT_WALL_DEPTH,
    windows: Array<IWindow> = [],
    doors: Array<IDoor> = [],
    radiators: Array<IRadiator> = [],
    attributes: Array<IAttribute> = [],
    uuid: string | undefined = undefined,
    passages: Array<IPassage> = [],
  ) {
    super(startPoint, endPoint, depth, EWall.STANDARD, windows, doors, radiators, attributes, uuid, passages);
    this.outerStartPoint = new Vector3();
    this.outerEndPoint = new Vector3();
    this.outerStartPoint2 = new Vector3();
    this.outerEndPoint2 = new Vector3();
    this.sideALength = 0;
    this.sideBLength = 0;
    this.updateWallLengths();
  }

  private isFreshlyCreated = (vec: Vector3): boolean => vec.x === 0 && vec.y === 0 && vec.z === 0;

  getOuterStartPoint(): Vector3 {
    return this.outerStartPoint;
  }

  setOuterStartPoint(outerStartPoint: Vector3): void {
    this.outerStartPoint = outerStartPoint;
  }

  getOuterEndPoint(): Vector3 {
    return this.outerEndPoint;
  }

  setOuterEndPoint(outerEndPoint: Vector3): void {
    this.outerEndPoint = outerEndPoint;
  }

  getOuterStartPoint2(): Vector3 {
    return this.outerStartPoint2;
  }

  setOuterStartPoint2(outerStartPoint2: Vector3): void {
    this.outerStartPoint2 = outerStartPoint2;
  }

  getOuterEndPoint2(): Vector3 {
    return this.outerEndPoint2;
  }

  setOuterEndPoint2(outerEndPoint2: Vector3): void {
    this.outerEndPoint2 = outerEndPoint2;
  }

  getSideALength(): number {
    return this.sideALength;
  }

  setSideALength(sideALength: number): void {
    this.sideALength = sideALength;
  }

  getSideBLength(): number {
    return this.sideBLength;
  }

  setSideBLength(sideBLength: number): void {
    this.sideBLength = sideBLength;
  }

  updateWallLengths() {
    this.sideALength = calculateWallLength(this.startPoint, this.endPoint) / UNIT_TO_METER_DIVISOR;
    if (!this.isFreshlyCreated(this.outerStartPoint) && !this.isFreshlyCreated(this.outerEndPoint)) {
      this.sideBLength = calculateWallLength(this.outerStartPoint, this.outerEndPoint) / UNIT_TO_METER_DIVISOR;
    }
  }

  setOuterPoints(outerStartPoint: Vector3, outerEndPoint: Vector3) {
    this.outerStartPoint = outerStartPoint;
    this.outerEndPoint = outerEndPoint;
    this.updateWallLengths();
  }

  setOuterPoints2(outerStartPoint2: Vector3, outerEndPoint2: Vector3) {
    this.outerStartPoint2 = outerStartPoint2;
    this.outerEndPoint2 = outerEndPoint2;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      outerStartPoint: { x: this.outerStartPoint.x, y: this.outerStartPoint.y, z: this.outerStartPoint.z },
      outerEndPoint: { x: this.outerEndPoint.x, y: this.outerEndPoint.y, z: this.outerEndPoint.z },
      outerStartPoint2: { x: this.outerStartPoint2.x, y: this.outerStartPoint2.y, z: this.outerStartPoint2.z },
      outerEndPoint2: { x: this.outerEndPoint2.x, y: this.outerEndPoint2.y, z: this.outerEndPoint2.z },
    };
  }
}

export default StandardWallModel;
